import React from "react";
import { Lock, Server, Cookie, KeyRound, MailCheck, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const PILLARS = [
  { icon: <Lock size={20}/>,        title: "Encrypted everywhere",
    body: "Every request between your device and MAVULA is protected with HTTPS/TLS. Our certificates are issued and monitored by an independent CA and renewed automatically." },
  { icon: <Server size={20}/>,      title: "Encrypted at rest",
    body: "Databases and backups are encrypted at rest. Personal information (names, emails, phones) is never stored in plain text where a third party can read it." },
  { icon: <KeyRound size={20}/>,    title: "Password hygiene",
    body: "Passwords are hashed with modern industry algorithms (bcrypt) — MAVULA staff cannot read your password. Reset tokens expire in 30 minutes and are single-use." },
  { icon: <ShieldCheck size={20}/>, title: "Rate limits + abuse guard",
    body: "Sign-up, login, and password-reset endpoints are rate-limited per IP. Repeated failed attempts back off automatically. Suspicious activity is logged for review." },
  { icon: <MailCheck size={20}/>,   title: "Email verification",
    body: "Withdrawals, password resets and account deletions all require confirmation from the account email — nothing sensitive happens on click alone." },
  { icon: <Cookie size={20}/>,      title: "Minimal cookies",
    body: "We use only what is strictly necessary for you to stay signed in and for us to prevent fraud. No third-party ad trackers. See the Cookie Policy for the full list." },
];

const ROADMAP = [
  "Two-factor authentication (2FA) via authenticator app — Q3 2026",
  "Passkey / hardware-key login — Q4 2026",
  "Independent security audit + public results — annually from 2027",
];

export default function SecurityPage() {
  return (
    <main data-testid="security-page">
      <section className="mv-green-bg text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ Security</div>
          <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[0.98]">
            Your account, <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>locked down.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-white/85 text-[15.5px] leading-relaxed">
            Property investment is a serious thing. Your login should be too. Here's exactly how MAVULA protects your account and your data.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PILLARS.map((p, i) => (
            <div key={p.title} className="mv-card p-6" data-testid={`security-pillar-${i}`}>
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mv-green-text mb-3"
                   style={{ background: "rgba(212,175,55,0.16)" }}>{p.icon}</div>
              <div className="font-display text-lg mv-green-text">{p.title}</div>
              <p className="mt-2 text-[13.5px]" style={{ color: "var(--mv-muted)" }}>{p.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 mv-card p-6" data-testid="security-roadmap">
          <div className="font-display text-lg mv-green-text">Security roadmap</div>
          <ul className="mt-3 space-y-2 text-[14px]" style={{ color: "var(--mv-ink)" }}>
            {ROADMAP.map((r) => (
              <li key={r} className="flex items-start gap-2">
                <ArrowRight size={14} className="mt-1 mv-green-text"/>{r}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-8 mv-card p-6" style={{ background: "linear-gradient(180deg, #fff, rgba(212,175,55,0.08))" }}>
          <div className="font-display text-lg mv-green-text">Found a vulnerability?</div>
          <p className="mt-2 text-[14px]" style={{ color: "var(--mv-muted)" }}>
            Please email <a href="mailto:Sebata@pulalove.com" className="underline mv-green-text">Sebata@pulalove.com</a> with details.
            We acknowledge within 2 working days and never take legal action against good-faith reporters.
          </p>
          <div className="mt-4">
            <Link to="/policies" className="btn-ghost" data-testid="security-back-to-policies">Read the full policies →</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
